import { z } from 'zod';
import { uuidSchema } from './common.js';
import { calculationBasisSchema } from './financials.js';
import { BULK_ENTRY_MAX_COUNT } from '../financials-constants.js';

/**
 * Wire DTOs for `GET/POST /api/client/data-health` (SIL-294, W2, spec slice
 * 2 `resources/plans/financials-hifi-flow-spec.md`). Mirrors the
 * `erp-coverage.ts` DTO shape (bare-JSON success, no `{data:...}` envelope).
 */

export const dataHealthAnomalyKindSchema = z.enum(['zombie', 'ghost', 'stale']);
export type DataHealthAnomalyKindDto = z.infer<typeof dataHealthAnomalyKindSchema>;

/** One anomaly row — an asset can appear more than once (once per kind it
 * currently exhibits; each (asset, kind) pair has its own ack scope). */
export const dataHealthAnomalySchema = z.object({
  assetId: uuidSchema,
  assetClassId: z.string().nullable(),
  serialNumber: z.string().nullable(),
  assetTag: z.string().nullable(),
  anomalyKind: dataHealthAnomalyKindSchema,
  evidenceAt: z.string(),
  netBookValueUsd: z.coerce.number().nullable(),
  telemetryLastSeenAt: z.string().nullable(),
  daysDark: z.number().nullable(),
});
export type DataHealthAnomalyDto = z.infer<typeof dataHealthAnomalySchema>;

export const dataHealthCountsSchema = z.object({
  zombie: z.number().int().nonnegative(),
  ghost: z.number().int().nonnegative(),
  stale: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});
export type DataHealthCounts = z.infer<typeof dataHealthCountsSchema>;

/** GET /api/client/data-health — no pagination v1 (spec: "document the same
 * ~2k-asset revisit threshold as 677" — pagination is a documented deferred
 * item, not built here). Counts are derived from the SAME query as
 * `anomalies` (spec "anomaly list + counts (from the same query)"). */
export const dataHealthResponseSchema = z.object({
  anomalies: z.array(dataHealthAnomalySchema),
  counts: dataHealthCountsSchema,
  basis: calculationBasisSchema.optional(),
  generatedAt: z.string(),
});
export type DataHealthResponse = z.infer<typeof dataHealthResponseSchema>;

/**
 * POST /api/client/data-health/ack (admin-only). Id-list resolution rule
 * (spec Tenant-isolation & authz invariants): `assetIds` resolves in ONE
 * GUC-scoped query against the CURRENT anomaly list for `anomalyKind` — any
 * id that doesn't currently resolve (wrong org, wrong kind, no anomaly)
 * rejects the WHOLE request with a uniform error (no per-id disclosure,
 * all-or-nothing — no row written). Reuses `BULK_ENTRY_MAX_COUNT` as the
 * same request-size cap the spec applies to every bulk write in this epic.
 */
export const dataHealthAckWriteSchema = z.object({
  assetIds: z.array(uuidSchema).min(1).max(BULK_ENTRY_MAX_COUNT),
  anomalyKind: dataHealthAnomalyKindSchema,
});
export type DataHealthAckWrite = z.infer<typeof dataHealthAckWriteSchema>;

export const dataHealthAckResponseSchema = z.object({
  ackedCount: z.number().int().nonnegative(),
});
export type DataHealthAckResponse = z.infer<typeof dataHealthAckResponseSchema>;
